import { faArrowAltToTop, faDatabase, faFlag, faMoon, faSun, faUserCircle, faUsers } from '@fortawesome/duotone-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    AppBar,
    Box,
    Button,
    darken,
    Divider,
    Drawer,
    Fade,
    IconButton,
    lighten,
    LinearProgress,
    Link,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    styled,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';

import React, { FunctionComponent, MouseEvent, ReactNode, useContext, useMemo, useRef, useState } from 'react';
// import logo from '@/assets/img/logo-small.png';
import { SourceDataVerificationPermission } from '@/api/dtos';
import AppSettings from '@/AppSettings';
import colorLogo from '@/assets/img/logo-h-c.png';
import whiteLogo from '@/assets/img/logo-h-w.png';
import Image from '@/components/layouts/Image';
import TrialContext from '@/contexts/data/TrialContext';
import TrialsContext from '@/contexts/data/TrialsContext';
import usePermissions from '@/hooks/utility/usePermissions';
import { isRestricted } from '@/restricted';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { faBars, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { AuthenticatedUserContext, ChosenThemeActionContext, ChosenThemeContext, ThemeName } from '@ngt/forms';
import { combinePaths } from '@servicestack/client';
import { useCallback } from 'react';
import { Outlet, Link as RouteLink, useLocation } from 'react-router-dom';
import ToggleFeedbackButton from '../error/ToggleFeedbackButton';
import ShowOnScroll from './ShowOnScroll';

export interface ILink
{
    url: string;
    icon: any;
    newTab: boolean;
    route: boolean;
    title: string;
}

interface ILayoutProps {
    links?: ILink[];
    children?: ReactNode;
}
interface IChosenThemeProps {
    themeMode?: ThemeName | undefined;
}

const StyledAppBar = styled(AppBar)<IChosenThemeProps>(({ theme, themeMode }) => ({
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: themeMode === 'dark' ?
        '#121212' :
        'white',
    backgroundImage: themeMode === 'dark' ?
        'linear-gradient(rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.09))' :
        undefined
}));

const StyledDrawerPaper = styled(Drawer)(({ theme, open }) => ({
   '& .MuiDrawer-paper': {
    backgroundColor:
        theme.palette.mode === 'dark'
        ? theme.palette.grey[900]
        : theme.palette.grey[100],
  backgroundImage: 'none', // disable built-in paper background texture
}


}));

const StyledToolbar = styled(Toolbar)<IChosenThemeProps>(({ theme, themeMode}) => ({
    background: themeMode === 'dark' ?
        undefined :
        theme.palette.common.white,
    color: themeMode === 'dark' ?
        undefined :
        theme.palette.primary.main,
    minHeight: `${theme.spacing(8)} !important`
}))

const StyledTitle = styled(Typography)<IChosenThemeProps>(({ theme, themeMode}) => ({
    color: themeMode === 'dark' ?
        theme.palette.common.white :
        theme.palette.primary.main,
    textDecoration: 'none',
    textTransform: 'uppercase'


}))

const drawerWidth = '240px';

const DefaultLayout: FunctionComponent<ILayoutProps> = ({ children }) => {
    const theme = useTheme();
    const { data: user } = useContext(AuthenticatedUserContext);

    const { data: trial, code: trialCode, loading: trialLoading } = useContext(TrialContext);

    const { 
        data: [
            hasEditConfiguration
        ] = [], 
        loading: permissionsLoading, 
        validating: permissionsValidating
    } = usePermissions(
        [
            SourceDataVerificationPermission.EditConfiguration
        ], 
        trialLoading,
        trial?.id
    );

    const showDrawer = !!trialCode;

    const  links = useMemo(() => {
        if (!trialCode) {
            return undefined;
        }

        const l = [
            {
                icon: faUsers,
                newTab: false,
                route: true,
                title: 'Participants',
                url: `/${trialCode}/participants`,
            },
            {
                icon: faFlag,
                newTab: false,
                route: true,
                title: 'Discrepancy Notes',
                url: `/${trialCode}/discrepancy-notes`,
            }
        ];

        if (hasEditConfiguration) {
            l.push({
                icon: faDatabase,
                newTab: false,
                route: true,
                title: 'Configurations',
                url: `/${trialCode}/configurations`,
            });
        }
            
        return l;
    }, [trialCode, hasEditConfiguration])

    const location = useLocation();

    const chosenTheme = useContext(ChosenThemeContext);
    const setChosenTheme = useContext(ChosenThemeActionContext);

    const { data: trials, loading: trialsLoading } = useContext(TrialsContext);

    
    const [routeLoaded, setRouteLoaded] = useState(false);
    const [routeProgress, setRouteProgress] = useState<number | undefined>(undefined);

    const isGreaterThanSmallBreakpoint = useMediaQuery(
        theme.breakpoints.up("md")
    );

    const changeTheme = useCallback(() => {
        setChosenTheme(chosenTheme === 'dark' ? 'light' : 'dark');
    }, [setChosenTheme, chosenTheme])

    const timeoutRef = useRef<number | undefined>()

    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

    // useEffect(() => {
    //     const routeChangeStart = () => {
    //         setAnchorEl(null);

    //         if (timeoutRef.current) {
    //             window.clearTimeout(timeoutRef.current);
    //             timeoutRef.current = undefined;
    //         }

    //         setRouteProgress(0);

    //         timeoutRef.current = window.setTimeout(() => {
    //             setRouteProgress(c => {
    //                 if (c && c >= 80) {
    //                     return c;
    //                 }

    //                 return (c ?? 0) + 10;
    //             })
    //         }, 500)


    //         setRouteLoaded(false) 
    //     };

    //     const routeChangeEnd = () => {
    //         setRouteLoaded(true);

    //         if (timeoutRef.current) {
    //             window.clearTimeout(timeoutRef.current);
    //             timeoutRef.current = undefined;

    //             setRouteProgress(c => {
    //                 timeoutRef.current = window.setTimeout(() => setRouteProgress(undefined), 500);
    
    //                 return 100;
    //             });
    //         }
    //     }

    //     router.events.on('routeChangeStart', routeChangeStart);
    //     router.events.on('routeChangeComplete', routeChangeEnd);
    //     router.events.on('routeChangeError', routeChangeEnd);

    //     return () => {
    //         if (timeoutRef.current) {
    //             window.clearTimeout(timeoutRef.current);
    //             timeoutRef.current = undefined;
    //         }

    //         router.events.off('routeChangeStart', routeChangeStart);
    //         router.events.off('routeChangeComplete', routeChangeEnd);
    //         router.events.off('routeChangeError', routeChangeEnd);
    //     };
    // }, [setRouteLoaded, setAnchorEl])


    const open = Boolean(anchorEl);

    const handleMenu = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const [drawOpen, setDrawOpen] = React.useState(false);

    const handleDrawerOpen = () => {
        setDrawOpen(true);
    };

    const handleDrawerClose = () => {
        setDrawOpen(false);
    };

    const drawEnabled = showDrawer;

    let returnToTop = (
        <ListItem 
            button 
            sx={{
                borderTop: `1px solid ${theme.palette.grey[300]}`,
                minHeight: '50px'
            }}
            onClick={() => { 
                window.scrollTo({ left: 0, top: 0, behavior: 'auto'});
            }}
        >
            <ListItemIcon 
                sx={{
                    fontSize: '24px'
                }}
            >
                <FontAwesomeIcon fixedWidth icon={faArrowAltToTop} />
            </ListItemIcon>
            <ListItemText 
                primary="Return to Top" 
                sx={{
                    display: !drawOpen ? 'none' : undefined
                }} 
            />
        </ListItem>
    )
    
    returnToTop = (
        <ShowOnScroll sx={{mt: 'auto'}}>
            <Box>
                {
                    drawOpen ?
                        returnToTop :
                        <Tooltip title="Return to Top" placement="right">
                            <Box>
                                {returnToTop}
                            </Box>
                        </Tooltip>
                }
            </Box>
        </ShowOnScroll>
    )

    return (
        <Box 
            sx={{
                display: 'flex',
                width: '100%'
            }}
        >
            {
                !isRestricted && (
                    <StyledAppBar
                        themeMode={chosenTheme}
                        position="fixed"
                        sx={{
                            zIndex: theme.zIndex.drawer + 1
                        }}
                    >
                        <StyledToolbar
                            themeMode={chosenTheme}
                        >
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                onClick={handleDrawerOpen}
                                edge="start"
                                sx={{
                                    marginRight: theme.spacing(2),
                                    display: drawOpen ? 'none' : undefined,
                                    visibility: drawEnabled ? undefined : 'hidden'
                                }}
                            >
                                <FontAwesomeIcon fixedWidth icon={faBars} />
                            </IconButton>
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                onClick={handleDrawerClose}
                                edge="start"
                                sx={{
                                    marginRight: theme.spacing(2),
                                    display: !drawOpen ? 'none' : undefined
                                }}
                            >
                                <FontAwesomeIcon fixedWidth icon={faTimes} />
                            </IconButton>
                            <Box 
                                sx={{
                                    alignSelf: 'center',
                                    lineHeight: 0
                                }}
                            >
                                <a href={AppSettings.trialPortalUrl}>
                                    <Image
                                        sx={{
                                            height: theme.spacing(8),
                                            marginRight: theme.spacing(2),
                                            display: { xs: 'none', sm: 'inlien' }
                                        }}
                                        alt="Breast Cancer Trials" 
                                        src={chosenTheme === 'dark' ? whiteLogo : colorLogo} 
                                    />
                                </a>
                            </Box>
                            <Link
                                component={RouteLink}
                                to='/'
                                sx={{
                                    marginRight: 'auto',
                                    textDecoration: 'none'
                                }}
                            >
                                {
                                    trial && (
                                        <>
                                            <Box
                                                sx={{
                                                    display: { xs: 'none', sm: 'inline' }
                                                }}
                                            >
                                                
                                                <StyledTitle
                                                    sx={{
                                                        display: { xs: 'none', md: 'inline', xl: 'inline' }
                                                    }}
                                                 
                                                    themeMode={chosenTheme}
                                                    variant="caption"
                                                >
                                                    Participant Data
                                                </StyledTitle>
                                            </Box>
                                            <Box >
                                                <StyledTitle 
                                                    sx={{
                                                        display: { xs: 'none', md: 'inline', xl: 'inline' }
                                                    }}
                                                    themeMode={chosenTheme}
                                                >
                                                    <strong>{trial.name}</strong>
                                                </StyledTitle>
                                            </Box>
                                        </>

                                    )
                                }
                                {
                                    !trial && (
                                        <StyledTitle 
                                            sx={{
                                                    display: { xs: 'none', sm: 'inline' }
                                            }}
                                            themeMode={chosenTheme}
                                            variant="h6"
                                        >
                                            <strong>Participant Data</strong>
                                        </StyledTitle>
                                    )
                                }
                            </Link>
                            <Button
                        
                            
                                id="btn-trials"
                                color="inherit"
                                aria-label="trial selection"
                                aria-controls="menu-trials"
                                aria-haspopup="true"
                                sx={{mr: 2, display: { xs: 'inline-flex', sm: 'inline-flex' }}}
                                onClick={handleMenu}
                                endIcon={<FontAwesomeIcon icon={faChevronDown} fixedWidth />}
                            >
                                {
                                    trial ?
                                        trial.name  :
                                        'No Trial Selected'
                                }
                            </Button>
                            <Menu
                                sx={{ mt: 5}}
                                id="menu-trials"
                                anchorEl={anchorEl}
                                keepMounted
                                transformOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                open={anchorEl?.id === 'btn-trials'}
                                onClose={handleClose}
                            >
                                {
                                    trials?.map((x, i, a) => {
                                        return (
                                            [
                                                <MenuItem key={x.code} component={RouteLink} to={`/${x.code}/participants`}>{x.name}</MenuItem>,
                                                i < (a.length - 1) ? <Divider key={`${x.code}-divider`} /> : null
                                            ]
                                        );
                                    })
                                }
                            </Menu>
                            <Tooltip 
                                title={chosenTheme === 'dark' ? 'Dark Mode' : 'Light Mode'}
                            >
                                <IconButton
                                    aria-label="theme"
                                    color="inherit"
                                    onClick={changeTheme}
                                >
                                    <FontAwesomeIcon icon={chosenTheme === 'dark' ? faMoon : faSun } />
                                </IconButton>
                            </Tooltip>
                            <ToggleFeedbackButton />
                            {
                                !!user && (
                                    <>
                                        <IconButton
                                            id="btn-auth"
                                            aria-label="account of current user"
                                            aria-controls="menu-auth"
                                            aria-haspopup="true"
                                            onClick={handleMenu}
                                            color="inherit">
                                            <FontAwesomeIcon icon={faUserCircle} />
                                        </IconButton>
                                        <Menu
                                            id="menu-auth"
                                            anchorEl={anchorEl}
                                            keepMounted
                                            sx={{ mt: 4 }}
                                            transformOrigin={{
                                                vertical: 'bottom',
                                                horizontal: 'left',
                                            }}
                                            open={anchorEl?.id === 'btn-auth'}
                                            onClose={handleClose}
                                        >
                                            <MenuItem>{user?.displayName ?? user?.userName}</MenuItem>
                                            <Divider />
                                            <MenuItem 
                                                onClick={() => { 
                                                    window.location.href = combinePaths(import.meta.env.VITE_API_URL, '/auth/logout'); 
                                                }}
                                            >
                                                Logout
                                            </MenuItem>
                                        </Menu>
                                    </>
                                )
                            }
                        </StyledToolbar>
                    </StyledAppBar>
                )
            }
            {
                drawEnabled && !isRestricted && (
                    <StyledDrawerPaper
                        style={{ zIndex: 1000 }}
                        open={drawOpen}
                        variant={isGreaterThanSmallBreakpoint ? "permanent" : "temporary"}

                        sx={{
                            width: drawOpen ? drawerWidth : 0,
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                            transition: drawOpen ? 
                                theme.transitions.create('width', {
                                    easing: theme.transitions.easing.sharp,
                                    duration: theme.transitions.duration.enteringScreen,
                                }) :
                                theme.transitions.create('width', {
                                    easing: theme.transitions.easing.sharp,
                                    duration: theme.transitions.duration.leavingScreen,
                                }),
                            overflowX: drawOpen ? 'hidden' : 'hidden',
                            [theme.breakpoints.up('sm')]: drawOpen ? 
                                undefined : 
                                {
                                    width: theme.spacing(8),
                                },
                            '& .MuiPaper-root': {
                                width: drawOpen ? drawerWidth : 0,
                                transition: drawOpen ? 
                                    theme.transitions.create('width', {
                                        easing: theme.transitions.easing.sharp,
                                        duration: theme.transitions.duration.enteringScreen,
                                    }) :
                                    theme.transitions.create('width', {
                                        easing: theme.transitions.easing.sharp,
                                        duration: theme.transitions.duration.leavingScreen,
                                    }),
                                overflowX: drawOpen ? 'hidden' : 'hidden',
                                [theme.breakpoints.up('sm')]: drawOpen ? 
                                    undefined : 
                                    {
                                        width: theme.spacing(8),
                                    },
                            }
                        }}
                        // classes={{
                        //     paper: clsx({
                        //         [classes.drawerOpen]: drawOpen,
                        //         [classes.drawerClose]: !drawOpen,
                        //     }),
                        // }}
                    >
                        <Box 
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                padding: theme.spacing(0, 1),
                                // necessary for content to be below app bar
                                ...(theme.mixins.toolbar as any),
                                '@media (min-width: 600px)': {
                                    minHeight: '64px'
                                }
                            }}
                        />
                        <List disablePadding sx={{ display: 'flex', flexDirection: 'column', height: '100%'}}>
                            {
                                links?.map((link, i) => {
                                    const active = location.pathname.startsWith(link.url);

                                    let linkComponent = null;
                                    if (link.newTab || !link.route) {
                                        linkComponent = (
                                            <ListItem 
                                                button 
                                                component="a" 
                                                href={link.url} 
                                                sx={{
                                                    borderBottom: `1px solid ${theme.palette.grey[800]}`,
                                                    minHeight: '50px'
                                                }} 
                                                key={i}
                                            >
                                                <ListItemIcon 
                                                    sx={{
                                                        fontSize: '24px'
                                                    }}
                                                >
                                                    <FontAwesomeIcon fixedWidth icon={link.icon} />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    color={theme.palette.mode === 'dark' ? theme.palette.grey[800]: theme.palette.grey[500]}
                                                    primary={link.title} 
                                                    sx={{
                                                        display: !drawOpen ? 'none' : undefined
                                                    }} 
                                                />
                                            </ListItem>
                                        );
                                    }
                                    else {
                                        linkComponent = (
                                            <ListItem 
                                                button
                                                component={RouteLink}
                                                to={link.url}
                                                sx={{
                                                    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[800]: theme.palette.grey[300]}`,
                                                    minHeight: '50px',
                                                    background: active ? theme.palette.mode === 'dark' ? darken(theme.palette.common.white, 0.75) : lighten(theme.palette.common.black, 0.92) : undefined
                                                }} 
                                                key={i}
                                            >
                                                <ListItemIcon 
                                                    sx={{
                                                        fontSize: '24px'
                                                    }}
                                                >
                                                    <FontAwesomeIcon fixedWidth icon={link.icon} />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary={<Typography color={theme.palette.mode === 'dark' ? theme.palette.grey[50]: theme.palette.grey[700]}>{link.title}</Typography>} 
                                                    sx={{
                                                        display: !drawOpen ? 'none' : undefined
                                                    }} 
                                                />
                                            </ListItem>
                                        );
                                    }


                                    if (drawOpen) {
                                        return linkComponent;
                                    }

                                    return (
                                        <Tooltip title={link.title} placement="right" key={i}>
                                            <Box>
                                                {linkComponent}
                                            </Box>
                                        </Tooltip>
                                    );
                                })
                            }
                            {returnToTop}
                        </List>
                    </StyledDrawerPaper>
                )
            }
            <Box 
                sx={{
                    flexGrow: 1,
                    padding: theme.spacing(3),
                    width: '80%'
                }}
            >
                {
                    !isRestricted && (
                        <Box 
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                padding: theme.spacing(0, 1),
                                // necessary for content to be below app bar
                                ...(theme.mixins.toolbar as any),
                                '@media (min-width: 600px)': {
                                    minHeight: '64px'
                                }
                            }}
                        />
                    )
                }
                { 
                    routeProgress !== undefined && (
                        <Box
                            sx={{
                                position: 'fixed',
                                left: 0,
                                right: 0,
                                top: '64px',
                                '@media (min-width: 0px)': {
                                    minHeight: '48px'
                                },
                                '@media (min-width: 600px)': {
                                    minHeight: '64px'
                                }
                            }}
                        >
                            <LinearProgress variant="determinate" color="secondary" value={routeProgress}  />
                        </Box>
                    ) 
                }
                {/* <Fade appear in={routeLoaded}> */}
                <Fade appear in={true}>
                    <div >
                        <Outlet />
                        {children}
                    </div>
                </Fade>
            </Box>
        </Box>
    );
};

export default DefaultLayout;